import { useGet } from "@/lib/api";
import { fmt, dirColor } from "@/lib/format";
import { AreaChart, Area, ResponsiveContainer, YAxis, Tooltip } from "recharts";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";
import { DxyGoldChart } from "@/components/DxyGoldChart";
import { CrossingBacktest } from "@/components/CrossingBacktest";
import { DxyGoldIntraday, DxyGoldRolling } from "@/components/DxyGoldDaily";

const META = {
  DXY: { color: "#6366F1", note: "ICE US Dollar Index (DXY). A stronger dollar pressures gold lower." },
  US02Y: { color: "#3B82F6", note: "Front-end rates track Fed policy expectations." },
  US10Y: { color: "#8B5CF6", note: "Long-end yields set gold's opportunity cost." },
};

export function DriversPage({ refreshKey }) {
  const { data, loading } = useGet("/market-drivers", refreshKey);
  if (loading || !data) return <div className="text-center text-[#6B7280] py-20 font-mono-num">Loading drivers…</div>;

  return (
    <div className="fade-up flex flex-col gap-4">
      <div>
        <h1 className="font-display text-2xl font-bold mb-1">Market Drivers</h1>
        <p className="text-xs text-[#6B7280] font-mono-num">Live inputs feeding Model B → gold transmission</p>
      </div>

      <DxyGoldChart refreshKey={refreshKey} />
      <CrossingBacktest refreshKey={refreshKey} />
      <DxyGoldIntraday refreshKey={refreshKey} />
      <DxyGoldRolling refreshKey={refreshKey} />

      {data.drivers.map((d) => {
        const meta = META[d.code] || { color: "#F59E0B", note: "" };
        const up = d.direction === "UP";
        const chartData = d.sparkline.map((v, i) => ({ i, v }));
        return (
          <section key={d.code} data-testid={`market-driver-${d.code.toLowerCase()}-card`}
            className="glass rounded-3xl p-5 hover-lift">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="font-display font-semibold">{d.name}</div>
                <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-mono-num">{d.code}</div>
              </div>
              <div className="text-right">
                <div className="font-mono-num text-2xl font-bold">{fmt(d.value, d.unit)}</div>
                <div className={`flex items-center justify-end gap-0.5 text-xs font-mono-num ${dirColor(d.direction)}`}>
                  {up ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                  {d.change_pct > 0 ? "+" : ""}{d.change_pct}%
                </div>
              </div>
            </div>
            <div className="h-24 -mx-1">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
                  <defs>
                    <linearGradient id={`grad-${d.code}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={meta.color} stopOpacity={0.4} />
                      <stop offset="100%" stopColor={meta.color} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <YAxis domain={["dataMin", "dataMax"]} hide />
                  <Tooltip
                    contentStyle={{ background: "#111827", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}
                    labelFormatter={() => ""}
                    formatter={(val) => [fmt(val, d.unit), d.code]}
                  />
                  <Area type="monotone" dataKey="v" stroke={meta.color} strokeWidth={2} fill={`url(#grad-${d.code})`} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <p className="text-[11px] text-[#9CA3AF] mt-2">{meta.note}</p>
          </section>
        );
      })}
    </div>
  );
}
